import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '@hooks/useAuth'
import { Button } from '@components/ui/Button'
import { Input } from '@components/ui/Input'
import { getImage } from '@assets/index'
import { environment } from '@config/environment'
import { EnvelopeIcon, LockClosedIcon } from '@heroicons/react/24/outline'

const loginSchema = z.object({
  email: z.string().email('Email invalide'),
  password: z.string().min(1, 'Le mot de passe est requis'),
})

type LoginForm = z.infer<typeof loginSchema>

export const LoginPage = () => {
  const { login, isLoading, user } = useAuth()
  const navigate = useNavigate()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // Obtenir les credentials de test selon l'environnement
  const testCredentials = environment.getTestCredentials()
  
  // Debug log pour vérifier les credentials
  console.log('🔐 Login page credentials:', {
    mode: environment.getConfig().mode,
    isLocal: environment.getConfig().isLocal,
    hasTestCredentials: !!testCredentials,
    email: testCredentials?.email
  })

  // Redirect to home if already authenticated
  useEffect(() => {
    if (user) {
      navigate('/')
    }
  }, [user, navigate])

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: testCredentials?.email || '',
      password: testCredentials?.password || '',
    },
  })

  const onSubmit = async (data: LoginForm) => {
    setErrorMessage(null)
    
    try {
      const success = await login(data.email, data.password)
      
      if (!success) {
        setErrorMessage('Identifiants incorrects')
      }
    } catch (error) {
      console.error('Login error:', error)
      setErrorMessage('Identifiants incorrects')
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center">
      <div className="flex flex-col items-center px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-sm">
          {/* Logo */}
          <div className="text-center mb-16">
            <img 
              src={getImage('logo')} 
              alt="SkeenFit" 
              className="h-24 w-auto mx-auto"
            />
          </div>

          {/* Form */}
          <div className="w-full">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
              {/* Email Field */}
              <Input
                {...register('email')}
                type="email"
                label="Email"
                placeholder="Adresse email"
                error={errors.email?.message}
                leftIcon={<EnvelopeIcon className="h-4 w-4" />}
                disabled={isLoading}
                autoComplete="email"
              />

              {/* Password Field */}
              <Input
                {...register('password')}
                isPassword
                label="Mot de passe"
                placeholder="Mot de passe"
                error={errors.password?.message}
                leftIcon={<LockClosedIcon className="h-4 w-4" />}
                disabled={isLoading}
                autoComplete="current-password"
              />

              {/* Forgot Password Link */}
              <div className="flex justify-end">
                <Link
                  to="/auth/forgot-password"
                  className="text-sm text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300"
                >
                  Mot de passe oublié ?
                </Link>
              </div>

              {/* Error Message */}
              {errorMessage && (
                <div className="text-center py-3">
                  <p className="text-sm text-red-600 dark:text-red-400">
                    {errorMessage}
                  </p>
                </div>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full"
                disabled={isLoading}
                loading={isLoading}
              >
                Connexion
              </Button>

              {/* First Connection Link */}
              <div className="text-center">
                <Link
                  to="/auth/define-password"
                  className="text-sm text-gray-600 hover:text-gray-500 dark:text-gray-400 dark:hover:text-gray-300"
                >
                  Première connexion
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}